import { State, Selector, Action, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { PerformanceService } from '../../services/performance/performance.service';
import { Report } from 'app/models/report/report.model';
import { GetRepReports, GetMoreTeamsToDisplay } from 'ng2-app/store/performance/performance.action';
import { Injectable } from '@angular/core';

/**
 * replace null values in the object with 0
 *
 * @param obj
 *  */
const replacenull = (obj: object) => {
  const keys = Object.keys(obj);
  keys.forEach(item => {
    if (obj[item] === null) {
      obj[item] = 0;
    }
  });
  return obj;
};

const viewFilters = {
  MY_TEAM: 'My Team',
  ALL_TEAMS: 'All Teams',
  ALL_REPS: 'All Reps'
};


export interface PerformanceStateModel {
  reports: Report[];
  repTeams: Report[];
  repsGroupedByTeam: any;
  loggedInTeamName: string;
  loading: boolean;
  otherTeams: string[];
  teamsToDisplay: any;
}

@State<PerformanceStateModel>({
  name: 'performance',
  defaults: {
    reports: [],
    repTeams: [],
    repsGroupedByTeam: {},
    loggedInTeamName: '',
    loading: true,
    otherTeams: [],
    teamsToDisplay: {}
  }
})

@Injectable()

export class PerformanceState {


  @Selector()
  static loading(state: PerformanceStateModel) {
    return state.loading;
  }


  @Selector()
  static loggedInTeamName(state: PerformanceStateModel) {
    return state.loggedInTeamName;
  }

  /**
   * returns the perfomance data for the logged in rep
   *
   * @param state
   */
  @Selector()
  static loggedInRepTeam(state: PerformanceStateModel) {
    return state.repTeams;
  }

  /**
   * returns all records for all reps
   *
   * @param state
   */
  @Selector()
  static reports(state: PerformanceStateModel) {
    return state.reports;
  }

  /**
   * returns all records for all reps grouped by team
   *
   * @param state
   */
  @Selector()
  static repsGroupedByTeam(state: PerformanceStateModel) {
    return state.repsGroupedByTeam;
  }

  /**
   * returns a list of teams to for display, starting with the first 3 teams.
   *
   * @param state
   */
   @Selector()
   static teamsToDisplay(state: PerformanceStateModel) {
     return state.teamsToDisplay;
   }

  /**
   * returns a list of teams to for display, starting with the first 3 teams.
   *
   * @param state
   */
  @Selector()
  static moreTeamsAvailable(state: PerformanceStateModel) {
    return state.teamsToDisplay.size < state.otherTeams.length;
  }

  constructor(
    private performanceService: PerformanceService,
  ) { }


  getGroupByTeams(reports: any, user: string) {
    const loggedInTeamName = reports.find(el => el.repName === user).team;
    const repTeams = reports.filter(el => el.team === loggedInTeamName);
    const repsGroupedByTeam = new Map<string, any>();
    reports.forEach((el: Report) => {
      if (repsGroupedByTeam.has(el.team)) {
        const arr = repsGroupedByTeam.get(el.team);
        repsGroupedByTeam.set(el.team, [...arr, el]);
      } else {
        repsGroupedByTeam.set(el.team, [el]);
      }
    });
    repsGroupedByTeam.delete(loggedInTeamName);
    const otherTeams = Array.from(repsGroupedByTeam.keys()).sort();
    const teamsToDisplay = this.getMoreTeams(new Map<string, any>(), otherTeams, repsGroupedByTeam, 3);
    return { loggedInTeamName, repTeams, repsGroupedByTeam, otherTeams, teamsToDisplay };
  }

  getMoreTeams(teamsToDisplay: Map<string, any>, otherTeams: string[], repsGroupedByTeam: Map<string, any>, numberTeams: number) {
    for (let i = 0; i < numberTeams; i++) {
      if (teamsToDisplay.size < otherTeams.length) {
        teamsToDisplay.set(otherTeams[teamsToDisplay.size], repsGroupedByTeam.get(otherTeams[teamsToDisplay.size]));
      }
    }
    return teamsToDisplay;
  }

  /**
   * This function will add more teams to the state
   *
   * @param ctx
   * @param param1
   */
   @Action(GetMoreTeamsToDisplay)
   getMoreTeamsToDisplay(
     ctx: StateContext<PerformanceStateModel>,
     { currentView, numberOfTeams }: GetMoreTeamsToDisplay) {
     const currState = ctx.getState();
     if (currState.teamsToDisplay.size < currState.otherTeams.length && currentView === viewFilters.ALL_TEAMS) {
         const teamsToDisplay = this.getMoreTeams(currState.teamsToDisplay,
                                                  currState.otherTeams,
                                                  currState.repsGroupedByTeam,
                                                  numberOfTeams);
         setTimeout(() => {
           ctx.patchState({ teamsToDisplay: teamsToDisplay });
         }, 200);
       }
   }

  /**
   * if the filteredView is 'All Teams' then massage the data accordingly
   *
   * @param ctx
   * @param param1
   */
  @Action(GetRepReports)
  getRepReports(ctx: StateContext<PerformanceStateModel>, { currentView, user, useNewReport }:
    GetRepReports) {
    const currState = ctx.getState();
    ctx.patchState({ loading: true });
    if (currState.reports.length === 0) { // data getting loaded first time
      return this.performanceService.getRepReports(useNewReport).pipe(catchError(err => throwError(() => err)),
        map(reports => {
          const modifiedReports = reports.map((report: Report) => {
            // These modifications are for the new reports
            // When the new reports are adopted we can rework the model to expect them
            if(report.id && report.teamName && report.name) {
              report.lenderUserId = report.id;
              report.team = report.teamName;
              report.repName = report.name;
            }
            return replacenull(report);
          });
          const repReport = { ...modifiedReports.find(elem => elem.repName === user['repName']) };
          if (repReport && Object.keys(repReport).length > 0) {
            // from modified reports get the logged in user report and move to the top
            const idx = modifiedReports.findIndex(elem => elem.repName === user['repName']);
            modifiedReports.splice(idx, 1);
            modifiedReports.unshift(repReport);
          } else {
            const nonSalesReport: Report = {
              lenderUserId: user['lenderUserId'],
              repName: user['repName'],
              team: 'Non-sales Lendio',
              leadsClaimed: 0,
              leadsAssigned: 0,
              textsSent: 0,
              textsReceived: 0,
              emailsSent: 0,
              emailsReceived: 0,
              outgoingCalls: 0,
              incomingCalls: 0,
              averageCallTime: 0,
              totalCallTime: 0,
              appsSent: 0
            };
            modifiedReports.unshift(nonSalesReport);
          }
          const {
            loggedInTeamName,
            repTeams,
            repsGroupedByTeam,
            otherTeams,
            teamsToDisplay
          } = this.getGroupByTeams(modifiedReports, user['repName']);

          if (currentView === viewFilters.MY_TEAM) {
            ctx.patchState({
              reports: modifiedReports, loading: false, loggedInTeamName, repTeams
            });
          } else if (currentView === viewFilters.ALL_REPS) {
            ctx.patchState({
              reports: modifiedReports, loading: false
            });
          } else if (currentView === viewFilters.ALL_TEAMS) {
            ctx.patchState({
              reports: modifiedReports, loading: false, loggedInTeamName, repTeams, repsGroupedByTeam, otherTeams, teamsToDisplay
            });
          }
        }));
    } else { // return existing reports
      if (currentView === viewFilters.MY_TEAM) {
        const { loggedInTeamName, repTeams, } = this.getGroupByTeams(currState.reports, user['repName']);
        setTimeout(() => {
          ctx.patchState({
            reports: currState.reports, loading: false, loggedInTeamName: loggedInTeamName, repTeams: repTeams
          });
        }
          , 350);
      } else if (currentView === viewFilters.ALL_REPS) {
        setTimeout(() => {
          ctx.patchState({
            reports: currState.reports, loading: false
          });
        }
          , 350);
      } else if (currentView === viewFilters.ALL_TEAMS) {
        const { loggedInTeamName, repTeams, repsGroupedByTeam, otherTeams, teamsToDisplay } =
        this.getGroupByTeams(currState.reports, user['repName']);
        setTimeout(() => {
          ctx.patchState({
            reports: currState.reports, loading: false, loggedInTeamName: loggedInTeamName, repTeams: repTeams,
            repsGroupedByTeam: repsGroupedByTeam, otherTeams: otherTeams, teamsToDisplay: teamsToDisplay
          });
        }, 350);
      }
    }
  }
}
